import React, { useState, useEffect } from "react";
import { TextField, Button, Box, Typography } from "@mui/material";
import styles from "./CouponCodeInput.module.css"; // Import CSS Modules

interface CouponCodeInputProps {
  onValidate: (couponCode: string) => void;
  onRemove?: () => void;
  loading?: boolean;
  appliedCouponCode?: string | null;
  discountAmount?: number;
  error?: string | null;
}

export const CouponCodeInput: React.FC<CouponCodeInputProps> = ({
  onValidate,
  onRemove,
  loading,
  appliedCouponCode,
  discountAmount,
  error,
}) => {
  const [couponCode, setCouponCode] = useState("");

  // 🔧 FIX: Sync local state with Redux state to clear input when coupon is removed
  useEffect(() => {
    // Clear the input field when no coupon is applied (coupon was removed)
    if (!appliedCouponCode) {
      setCouponCode("");
    }
  }, [appliedCouponCode]);

  const handleValidate = () => {
    if (couponCode.trim()) {
      onValidate(couponCode.trim());
    }
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    }
  };

  // If coupon is applied, show applied coupon info
  if (appliedCouponCode) {
    return (
      <Box className={styles.couponContainer}>
        <Typography variant="subtitle1" className={styles.couponTitle}>
          Cupón aplicado
        </Typography>
        <Box className={styles.appliedCoupon}>
          <Box className={styles.couponInfo}>
            <Typography variant="body2" className={styles.couponCode}>
              {appliedCouponCode}
            </Typography>
            {discountAmount && discountAmount > 0 && (
              <Typography variant="body2" className={styles.discountAmount}>
                Descuento: -S/ {discountAmount.toFixed(2)}
              </Typography>
            )}
          </Box>
          <Button
            onClick={handleRemove}
            variant="outlined"
            size="small"
            className={styles.removeButton}
          >
            Quitar
          </Button>
        </Box>
        {error && (
          <Typography variant="body2" className={styles.errorText}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box className={styles.couponContainer}>
      <Typography variant="subtitle1" className={styles.couponTitle}>
        ¿Tienes un cupón de descuento?
      </Typography>
      <Box className={styles.couponActions}>
        <TextField
          label="Ingresa tu cupón"
          variant="outlined"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value)}
          fullWidth
          className={styles.styledTextField} // Apply class for TextField wrapper
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "var(--simple-white)",
              "& fieldset": {
                borderColor: "var(--denim-blue-light-1)",
              },
              "&:hover fieldset": {
                borderColor: "var(--denim-blue)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "var(--denim-blue-dark-1)",
              },
            },
            "& .MuiInputLabel-root": {
              color: "var(--text-color)",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "var(--denim-blue-dark-1)",
            },
          }}
        />
        <Button
          onClick={handleValidate}
          disabled={loading || !couponCode.trim()}
          className={styles.validateButton} // Apply class for Button
          variant="contained" // Keep variant for base MUI styling if desired
        >
          {loading ? "Validando..." : "Aplicar"}
        </Button>
      </Box>
      {error && (
        <Typography variant="body2" className={styles.errorText}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default CouponCodeInput;
